import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { initializeAuth, getUserManager, getKeycloakUserId } from '../config/keycloak';
import { AutoSsoSuppressKey, AUTO_SSO_GUARD_KEY, securityParamsKey } from '../constants/authorization';
import { currentReturnTo, safeReturnTo } from '../utils/postLoginRedirect';

const AuthContext = createContext(null);

function toAppUser(oidcUser) {
  return {
    id: oidcUser.profile?.sub,
    name: oidcUser.profile?.name || oidcUser.profile?.preferred_username || 'User',
    email: oidcUser.profile?.email || '',
    photoURL: oidcUser.profile?.picture || null,
    keycloakId: oidcUser.profile?.sub,
  };
}

// Reaching an authenticated state resets both auto-SSO guards so a later logout
// can auto-redirect again. Lives here (not in the login page) because
// discover-chat unmounts <Login> the moment `user` is set.
function clearAutoSsoGuards() {
  sessionStorage.removeItem(AUTO_SSO_GUARD_KEY);
  sessionStorage.removeItem(AutoSsoSuppressKey);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [keycloakUser, setKeycloakUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    initAuth();

    // Keep React state in sync with oidc-client-ts's OWN background renewal.
    // Without these, a successful silent refresh updates only its internal
    // WebStorageStateStore — this component's `keycloakUser`/`user` state
    // (and therefore getUserIdDiscover()) would keep serving the pre-refresh
    // token until the next full page load, which could itself be stale by
    // then. addUserLoaded fires on every renewal (silent or interactive);
    // addSilentRenewError logs a failed background attempt instead of
    // failing silently with no signal at all.
    const manager = getUserManager();
    const handleUserLoaded = (loadedUser) => {
      clearAutoSsoGuards();
      setKeycloakUser(loadedUser);
      setUser(toAppUser(loadedUser));
    };
    const handleSilentRenewError = (err) => {
      console.error('Silent renew error:', err);
    };
    manager.events.addUserLoaded(handleUserLoaded);
    manager.events.addSilentRenewError(handleSilentRenewError);
    return () => {
      manager.events.removeUserLoaded(handleUserLoaded);
      manager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, []);

  const initAuth = async () => {
    try {
      initializeAuth();
      const manager = getUserManager();
      const currentUser = await manager.getUser();

      if (currentUser && !currentUser.expired) {
        clearAutoSsoGuards();
        setKeycloakUser(currentUser);
        setUser(toAppUser(currentUser));
      }
    } catch (err) {
      console.error('Auth init error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (idpHint, returnTo = currentReturnTo()) => {
    // Explicit sign-in intent clears any post-logout suppression.
    sessionStorage.removeItem(AutoSsoSuppressKey);
    const manager = getUserManager();
    await manager.signinRedirect({
      // The page being left, carried through Keycloak in the OIDC state store
      // so /callback can return to it instead of dropping everyone on "/".
      // Sign-in started from the app root sends "/" and nothing changes; a
      // shared-chat link that required a sign-in is the case this exists for.
      state: { returnTo: safeReturnTo(returnTo) },
      ...(idpHint ? { extraQueryParams: { kc_idp_hint: idpHint } } : {}),
    });
  };

  const signOut = async () => {
    const manager = getUserManager();
    setUser(null);
    setKeycloakUser(null);
    // Security params live until logout (see AgentConfigContext).
    if (user?.id) localStorage.removeItem(securityParamsKey(user.id));
    // Set BEFORE the redirect so the post-logout landing on the login page does
    // not immediately auto-redirect the user back in via a still-live upstream
    // session (the logout→relogin loop).
    sessionStorage.setItem(AutoSsoSuppressKey, '1');
    await manager.signoutRedirect();
  };

  const handleCallback = async () => {
    try {
      const manager = getUserManager();
      const callbackUser = await manager.signinRedirectCallback();

      if (callbackUser) {
        setKeycloakUser(callbackUser);
        setUser(toAppUser(callbackUser));
      }

      return callbackUser;
    } catch (err) {
      console.error('Callback error:', err);
      setError(err.message);
      throw err;
    }
  };

  // Memoised on `keycloakUser`, not for speed: a consumer that puts either of these in a
  // useCallback/useEffect dependency list gets a NEW function on every provider render
  // otherwise, so its effect re-runs, sets state, re-renders the provider, and loops —
  // issuing a request per iteration. Keyed on keycloakUser so a silent renewal still
  // produces fresh accessors (see the addUserLoaded handler above, which is what makes
  // that state change at all).
  const getAccessToken = useCallback(() => {
    return keycloakUser?.access_token || null;
  }, [keycloakUser]);

  // Returns the Keycloak user ID (sub claim) as user_id_discover
  const getUserIdDiscover = useCallback(() => {
    return getKeycloakUserId(keycloakUser);
  }, [keycloakUser]);

  const value = {
    user,
    keycloakUser,
    loading,
    error,
    signIn,
    signOut,
    handleCallback,
    getAccessToken,
    getUserIdDiscover,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
